var _a;
import { __assign, __awaiter, __generator } from "tslib";
import MockAdapter from 'axios-mock-adapter';
import { SearchApi } from '../api';
import { DocumentParser, SuggestionsParser, parseDocumentTypes, parseTotals, } from './parsers';
import { SearchDocumentType, } from './sdk.types';
import { mockApi } from './mockApi';
import { apiErrorParser } from './parsers/apiErrorParser';
function getSearchApiDocumentType(documentType) {
    if (documentType === SearchDocumentType.All) {
        return;
    }
    return documentType;
}
var getQuery = function (query) { return query || '*'; };
var ClientSearchSDK = /** @class */ (function () {
    function ClientSearchSDK(config) {
        this.language = config.language;
        this.api = new SearchApi({
            token: config.token,
            timeout: config.timeout,
            baseURL: config.baseURL,
        });
        this.apiMockAdapter = null;
        this.documentParser = new DocumentParser(config.siteBaseURL, config.useEmptyImages);
        this.suggestionsParser = new SuggestionsParser(config.siteBaseURL, config.language, config.useEmptyImages);
    }
    ClientSearchSDK.getDocumentTypeOrderIndex = function (documentType) {
        var index = ClientSearchSDK.documentTypesOrder[documentType.documentType];
        return index === undefined ? Number.MAX_SAFE_INTEGER : index;
    };
    ClientSearchSDK.documentTypesComparator = function (a, b) {
        return (ClientSearchSDK.getDocumentTypeOrderIndex(a) -
            ClientSearchSDK.getDocumentTypeOrderIndex(b));
    };
    ClientSearchSDK.prototype.setToken = function (token) {
        this.api.setToken(token);
    };
    ClientSearchSDK.prototype.getInstance = function () {
        return this.api.getInstance();
    };
    ClientSearchSDK.prototype.getDocumentTypes = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.getDocumentTypes()];
                    case 1:
                        response = _a.sent();
                        // NOTE: DocumentType.ALL is added at `parseDocumentTypes` if there is more than 1 document type
                        return [2 /*return*/, parseDocumentTypes(response.data.sources).sort(ClientSearchSDK.documentTypesComparator)];
                }
            });
        });
    };
    ClientSearchSDK.prototype.getTotals = function (searchRequest) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.search(__assign(__assign({}, searchRequest), { documentType: undefined, language: this.language, query: getQuery(searchRequest.query), paging: {
                                skip: 0,
                                limit: 0,
                            } }))];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, parseTotals(response.data)];
                }
            });
        });
    };
    ClientSearchSDK.prototype.search = function (searchRequest) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var response, ex_1;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.api.search(__assign(__assign({}, searchRequest), { documentType: getSearchApiDocumentType(searchRequest.documentType), query: getQuery(searchRequest.query), language: this.language, paging: searchRequest.paging && {
                                    skip: (searchRequest.paging.page - 1) * searchRequest.paging.pageSize,
                                    limit: searchRequest.paging.pageSize,
                                } }))];
                    case 1:
                        response = _b.sent();
                        return [2 /*return*/, {
                                documents: response.data.documents.map(function (document) {
                                    return _this.documentParser.parse(document);
                                }),
                                totalResults: response.data.nextPage ? response.data.nextPage.total : 0,
                            }];
                    case 2:
                        ex_1 = _b.sent();
                        return [2 /*return*/, {
                                errorDetails: apiErrorParser((_a = ex_1 === null || ex_1 === void 0 ? void 0 : ex_1.response) === null || _a === void 0 ? void 0 : _a.data),
                                isError: true,
                            }];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    ClientSearchSDK.prototype.useDemoContent = function (options) {
        if (!this.apiMockAdapter) {
            this.apiMockAdapter = new MockAdapter(this.api.getInstance());
        }
        else {
            this.apiMockAdapter.reset();
        }
        mockApi(this.apiMockAdapter, options);
    };
    ClientSearchSDK.prototype.getFederatedSuggestions = function (suggestionsRequest) {
        return __awaiter(this, void 0, void 0, function () {
            var response, documentsMapper, results;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.api.getFederatedSuggestions({
                            query: suggestionsRequest.query || '*',
                            limit: suggestionsRequest.limit || 10,
                            language: this.language,
                        })];
                    case 1:
                        response = _a.sent();
                        documentsMapper = function (document) {
                            return _this.documentParser.parse(document);
                        };
                        results = response.data.results
                            .sort(ClientSearchSDK.documentTypesComparator)
                            .map(function (sample) { return (__assign(__assign({}, sample), { title: _this.suggestionsParser.getDocumentTypeTitle(sample.documentType), documents: sample.documents.map(documentsMapper) })); });
                        return [2 /*return*/, {
                                results: results,
                            }];
                }
            });
        });
    };
    ClientSearchSDK.prototype.getSample = function (sampleRequest) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var response, documentsMapper_1, results, ex_2;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.api.getSample(__assign(__assign({}, sampleRequest), { query: sampleRequest.query && getQuery(sampleRequest.query), language: this.language }))];
                    case 1:
                        response = _b.sent();
                        documentsMapper_1 = function (document) {
                            return _this.documentParser.parse(document);
                        };
                        results = response.data.results
                            .sort(ClientSearchSDK.documentTypesComparator)
                            .map(function (sample) { return (__assign(__assign({}, sample), { documents: sample.documents.map(documentsMapper_1) })); });
                        return [2 /*return*/, {
                                results: results,
                            }];
                    case 2:
                        ex_2 = _b.sent();
                        return [2 /*return*/, {
                                errorDetails: apiErrorParser((_a = ex_2 === null || ex_2 === void 0 ? void 0 : ex_2.response) === null || _a === void 0 ? void 0 : _a.data),
                                isError: true,
                            }];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    ClientSearchSDK.prototype.onError = function (onError) {
        var axiosInstance = this.api.getInstance();
        var onFulfilled = function (config) { return config; };
        var onRejected = function (error) {
            onError(error);
            return Promise.reject(error);
        };
        axiosInstance.interceptors.request.use(onFulfilled, onRejected);
        axiosInstance.interceptors.response.use(onFulfilled, onRejected);
    };
    ClientSearchSDK.documentTypesOrder = (_a = {},
        _a[SearchDocumentType.All] = 0,
        _a[SearchDocumentType.Pages] = 1,
        _a[SearchDocumentType.Products] = 2,
        _a[SearchDocumentType.Blogs] = 3,
        _a[SearchDocumentType.Forums] = 4,
        _a[SearchDocumentType.Bookings] = 5,
        _a[SearchDocumentType.Events] = 6,
        _a);
    return ClientSearchSDK;
}());
export { ClientSearchSDK };
//# sourceMappingURL=sdk.js.map